import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { memo, useMemo } from 'react';
import { DateRange } from 'react-aria-components';
import { Bar } from 'react-chartjs-2';

import { useTimezone } from '@/hooks';
import { formatDateShort } from '@/utils';

import { useStatistics } from './useStatistics';
import { getData, MetricType } from './utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, BarElement, Title, ChartTooltip, Legend);

export type GlobalGraphProps = {
  dateRange: DateRange | null;
  dateSelection: number | string;
  selectedMetric: MetricType;
};

const IGlobalGraph = ({ dateRange, dateSelection, selectedMetric }: GlobalGraphProps) => {
  const timezone = useTimezone();

  const { statistics } = useStatistics({ dateRange, dateSelection });

  const { labels, runsPerDay, automationPerDay, fullAutomationPerDay, flaggedPerDay, editsPerDay } = useMemo(() => {
    if (!dateRange) return {};

    const perDay = statistics?.statistics.globalPerDay;
    const labels = [];
    const runsPerDay = [];
    const automationPerDay = [];
    const fullAutomationPerDay = [];
    const flaggedPerDay = [];
    const editsPerDay = [];

    const start = dateRange.start.toDate(timezone);
    const end = dateRange.end.toDate(timezone);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}`;
      const data = getData(perDay?.[isoDate]);

      labels.push(formatDateShort(d));
      runsPerDay.push(data.numDocuments);
      automationPerDay.push(data.fractionValuesAutomated);
      fullAutomationPerDay.push(data.fractionDocumentsAutomated);
      flaggedPerDay.push(data.fractionValuesFlagged);
      editsPerDay.push(data.fractionValuesEdited);
    }

    return {
      labels,
      runsPerDay,
      automationPerDay,
      fullAutomationPerDay,
      flaggedPerDay,
      editsPerDay,
    };
  }, [dateRange, statistics, timezone]);

  const selectedData = useMemo(() => {
    let data;

    switch (selectedMetric) {
      case 'runs':
        data = runsPerDay;
        break;
      case 'automation':
        data = automationPerDay;
        break;
      case 'fullAutomation':
        data = fullAutomationPerDay;
        break;
      case 'flagged':
        data = flaggedPerDay;
        break;
      case 'edits':
        data = editsPerDay;
        break;
      default:
        data = runsPerDay;
    }

    return data;
  }, [automationPerDay, editsPerDay, flaggedPerDay, fullAutomationPerDay, runsPerDay, selectedMetric]);

  const globalOptions = useMemo(() => {
    let yAxisConfig;
    if (selectedMetric === 'runs') {
      yAxisConfig = {
        ticks: {
          callback: (value: number) => {
            return value.toFixed(0).toString();
          },
          stepSize: 1,
        },
        beginAtZero: true,
      };
    } else {
      yAxisConfig = {
        ticks: {
          callback: (value: number) => {
            if (value > 1) return null;
            return `${(value * 100).toFixed(0)}%`;
          },
          stepSize: 1,
        },
        min: 0,
        max: 1.1,
        suggestedMin: 0,
        suggestedMax: 1.1,
        beginAtZero: true,
      };
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed.y;
              if (selectedMetric === 'runs') {
                return `  ${value.toFixed(0)} runs`;
              }
              return `  ${(value * 100).toFixed(1)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: true,
            drawOnChartArea: true,
            drawTicks: true,
            color: (context) => {
              const index = context.index;
              const dataLength = context.chart.data.labels?.length || 0;
              // Only show grid lines for start, middle, and end
              if (index === 0 || index === dataLength - 1) {
                return 'rgba(0, 0, 0, 0.1)';
              }
              return 'transparent';
            },
          },
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            callback: function (value, index, ticks) {
              const dataLength = this.chart.data.labels?.length || 0;
              if (index === 0 || index === dataLength - 1) {
                return this.getLabelForValue(value);
              }
              return '';
            },
          },
        },
        y: {
          ...yAxisConfig,
          display: true,
        },
      },
      datasets: {
        bar: {
          barPercentage: 1.0,
          categoryPercentage: 1.0,
        },
      },
      interaction: {
        mode: 'index',
        intersect: false,
      },
      elements: {
        point: {
          radius: 0,
          hoverRadius: 3,
        },
      },
    };
  }, [selectedMetric]);

  const globalData = useMemo(() => {
    return {
      labels,
      datasets: [
        {
          data: selectedData,
          borderColor: '#84CAFF',
          borderWidth: 2,
          backgroundColor: '#84CAFF',
          fill: true,
          tension: 0.2,
          pointRadius: 0,
          pointBackgroundColor: '#84CAFF',
        },
      ],
    };
  }, [labels, selectedData]);

  return <Bar options={globalOptions} data={globalData} />;
};

export const GlobalGraph = memo(IGlobalGraph) as typeof IGlobalGraph;
